/**
 * SKU utility functions for device SKU tracking
 */
import { skuCatalog } from "../models/enums";

export const getDeviceModelDisplayName = (
  skuNumber: string | null | undefined,
  makeModelId: string | null | undefined
): string => {
  if (!makeModelId) return "";

  if (skuNumber && skuCatalog[skuNumber]) {
    const match = skuCatalog[skuNumber].subComponents.find(
      c => c.makeModelId === makeModelId
    );

    if (match) return match.name;
  }

  for (const sku of Object.values(skuCatalog)) {
    const match = sku.subComponents.find(c => c.makeModelId === makeModelId);

    if (match) return match.name;
  }

  return makeModelId;
};

import {
  SKU_DEVICE_TYPE_LABELS,
  SKU_MAKE_MODEL_TO_TYPE,
} from "../../pages/system-settings/tabs/skus/variables/skuData";

// Get display label for SKU
export const getSKUDisplayLabel = (
  configurationName: string | null | undefined
): string => {
  // Empty/null = no SKU assigned
  if (!configurationName) return "";

  if (configurationName === "000-000") return "Legacy";

  if (configurationName === "LEGACY") return "Legacy";

  // Return the configuration name as-is (stored from DynamoDB)
  return configurationName;
};

// Check if device is explicitly marked as Legacy
export const isLegacySKU = (
  configurationName: string | null | undefined
): boolean => {
  return configurationName === "LEGACY" || configurationName === "000-000";
};

// Check if SKU is explicitly set (not null/undefined/empty)
export const isSKUAssigned = (
  configurationName: string | null | undefined
): boolean => {
  return !!configurationName && configurationName.trim() !== "";
};

/**
 * Returns a friendly display label for a device's makeModelId.
 * For SKU devices, returns the component type label (e.g., "Optical Camera").
 * For legacy devices, returns the makeModelId as-is (caller should use DeviceMake.model).
 * @param {string|null|undefined} makeModelId - The make/model ID of the device.
 * @param {string|null|undefined} skuNumber - The SKU number, if the device was created via a SKU.
 * @return {string} A display-friendly string label.
 */
export const getDeviceTypeLabel = (
  makeModelId: string | null | undefined,
  skuNumber: string | null | undefined
): string => {
  if (!makeModelId) return "";

  // For SKU devices, use the friendly label
  if (skuNumber) {
    const componentType = SKU_MAKE_MODEL_TO_TYPE[makeModelId];

    if (componentType) {
      return SKU_DEVICE_TYPE_LABELS[componentType] ?? makeModelId;
    }
  }

  // For legacy devices, return makeModelId (DeviceMakes provides the display name elsewhere)
  return makeModelId;
};
